import React from "react";
import { Box, Spinner, Text, Tooltip } from "@chakra-ui/react";
import { ResponsivePie } from "@nivo/pie";
import { nivo_theme } from "./chart-utils";

/**
 * A horseshoe/gauge pie chart with completed/total count centered inside.
 * @param {Object[]} data [{id, value, color}]
 * @param {number} completed The count of completed items to show
 * @param {string} completedLabel Tooltip for the completed number
 * @param {string} totalTooltip Tooltip for the total number
 * @param {function} onClick datum -> void E.g. use datum.id
 * @param {string} totalLabel What are we counting? e.g. "Sources"
 * @param {boolean} animate If true, pulse the completed number to indicate activity
 */
export default function PieDialWithTotal({ data, completed, completedLabel, totalTooltip, onClick, totalLabel, width = "100%", animate }) {
  // filter zeros (to avoid pad-angle making a not-quite-full semi-circle)
  const filteredData = data.filter((d) => d.value > 0);
  const total = filteredData.reduce((acc, d) => acc + d.value, 0);

  return (
    <Box width={width} sx={{ aspectRatio: "5 / 3" }} maxHeight="300px" position="relative">
      <ResponsivePie
        data={filteredData}
        innerRadius={0.75}
        padAngle={2}
        cornerRadius={6}
        colors={(d) => d.data.color}
        startAngle={-130}
        endAngle={130}
        enableArcLabels={false}
        enableArcLinkLabels={false}
        onClick={onClick}
        theme={nivo_theme}
        motionConfig={animate ? "gentle" : "default"}
        tooltip={({ datum }) => (
          <Box display="flex" alignItems="center" gap={2} bg="white" border="1px solid #85C22A" borderRadius="8px" px="12px" py="8px" fontFamily="Plus Jakarta Sans, sans-serif">
            <Box w={3} h={3} borderRadius="full" bg={datum.color} flexShrink={0} />
            <Text fontSize="sm" fontWeight="600">{datum.data.label || datum.id}: {datum.value}</Text>
          </Box>
        )}
      />
      <Box position="absolute" top="0" left="0" right="0" bottom="0" display="flex" alignItems="center" justifyContent="center" pointerEvents="none">
        <Box textAlign="center">
          {completed != null ? (
            <Box lineHeight="1">
              <Tooltip label={completedLabel} isDisabled={!completedLabel} hasArrow>
                <Text as="span" fontSize="4xl" fontWeight="bold" color="#25C469">{completed}</Text>
              </Tooltip>
              <Text as="span" fontSize="lg" fontWeight="normal" color="gray.400"> of </Text>
              <Tooltip label={totalTooltip} isDisabled={!totalTooltip} hasArrow>
                <Text as="span" fontSize="4xl" fontWeight="bold">{total}</Text>
              </Tooltip>
              {animate && (
                <Box display="flex" justifyContent="center" mt={1}>
                  <Spinner size="sm" color="#25C469" speed="0.8s" thickness="2px" />
                </Box>
              )}
            </Box>
          ) : (
            <Text fontSize="4xl" fontWeight="bold" lineHeight="1">
              {total}
            </Text>
          )}
          {totalLabel && (
            <Text fontSize="sm" color="gray.500" mt={1}>
              {totalLabel}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
}
